import { Suspense } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Spin, Alert, Typography } from 'antd'
import { apiService } from '@/lib/api'

const { Title, Text } = Typography

function AuthCallbackContent() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      const errorParam = searchParams.get('error')
      if (errorParam) {
        setError(`Authentication failed: ${errorParam}`)
        setLoading(false)
        return
      }

      const token = searchParams.get('token')
      const email = searchParams.get('email')
      const name = searchParams.get('name')
      const provider = searchParams.get('provider')
      const needsOnboarding = searchParams.get('needs_onboarding')

      if (token && email) {
        try {
          localStorage.setItem('authToken', token)

          await new Promise(resolve => setTimeout(resolve, 100))

          const profileResponse = await apiService.getProfile()

          console.log('Profile response:', profileResponse)

          if (profileResponse.success && profileResponse.user) {
            const userData = {
              ...profileResponse.user,
              needs_onboarding: profileResponse.needs_onboarding
            }
            localStorage.setItem('user', JSON.stringify(userData))

            if (profileResponse.needs_onboarding) {
              navigate('/onboarding')
            } else {
              navigate('/dashboard')
            }
          } else {
            console.error('Profile fetch failed:', profileResponse)
            const userFromParams = {
              email: email,
              full_name: name || '',
              first_name: name?.split(' ')[0] || '',
              last_name: name?.split(' ').slice(1).join(' ') || '',
              provider: provider || 'google_oauth2',
              role: 'user',
              is_active: true,
              needs_onboarding: needsOnboarding === 'true' || needsOnboarding === '1'
            }
            localStorage.setItem('user', JSON.stringify(userFromParams))

            if (needsOnboarding === 'true' || needsOnboarding === '1') {
              navigate('/onboarding')
            } else {
              navigate('/dashboard')
            }
          }
          return
        } catch (err) {
          console.error('Auth callback error:', err)
          setError(err instanceof Error ? err.message : 'Failed to process authentication')
          setLoading(false)
          return
        }
      }

      const code = searchParams.get('code')
      const state = searchParams.get('state')

      if (code) {
        try {
          const response = await apiService.handleOAuthCallback(code, state || undefined)

          if (response.success && response.data) {
            localStorage.setItem('authToken', response.data.token)

            const profileResponse = await apiService.getProfile()

            if (profileResponse.success && profileResponse.user) {
              const userData = {
                ...profileResponse.user,
                needs_onboarding: profileResponse.needs_onboarding
              }
              localStorage.setItem('user', JSON.stringify(userData))

              if (profileResponse.needs_onboarding) {
                navigate('/onboarding')
              } else {
                navigate('/dashboard')
              }
            } else {
              localStorage.setItem('user', JSON.stringify(response.data.user))

              if (response.data.user.needs_onboarding) {
                navigate('/onboarding')
              } else {
                navigate('/dashboard')
              }
            }
          } else {
            setError(response.message || 'Authentication failed')
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'An unexpected error occurred')
        }
      } else if (!token) {
        setError('No authentication data received')
      }

      setLoading(false)
    }

    handleCallback()
  }, [searchParams, navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <Title level={3} className="mt-4">
            Completing Authentication...
          </Title>
          <Text type="secondary">Please wait while we log you in</Text>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Alert
            message="Authentication Error"
            description={error}
            type="error"
            showIcon
            className="mb-4"
          />
          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-cyan-400 hover:text-cyan-300 underline"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default function AuthCallback() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
          <div className="text-center">
            <Spin size="large" />
            <Title level={3} className="mt-4">
              Loading...
            </Title>
          </div>
        </div>
      }
    >
      <AuthCallbackContent />
    </Suspense>
  )
}
